import { Controller } from "@hotwired/stimulus"

// Drives the in-page anchor navigation bar on product PDPs:
//
// - highlights the link pointing at the section currently in view
//   (active state persists from one anchored section's top to the next,
//   even across unlinked intermediate content)
// - prunes links whose target section is not in the DOM, keeping the
//   nav in sync with the actual page content without per-page guards
// - optionally toggles `.visible` / `.invisible` when the first tracked
//   section enters/leaves the viewport (opt-in via `firstSection`)
// - optionally force-hides the bar when an external element matching
//   `hideWhenSelector` is present (the venue & activity quote sidebar
//   pins to the bottom and sets a class that we observe here)
// - tracks click events through Segment via `data-track-label`
export default class extends Controller {
  static targets = ["link"]
  static values = {
    firstSection: String,
    pageBottomSelector: { type: String, default: ".page-wrapper" },
    hideWhenSelector: String,
  }

  connect() {
    this.pruneOrphanLinks()
    this.onScroll = this.onScroll.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.onScroll()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
  }

  trackClick(event) {
    const link = event.currentTarget
    if (link.classList.contains("active")) return

    const label = link.dataset.trackLabel
    if (!label || !window.analytics) return

    window.analytics.track("Clicked on Page", { name: "Navigation", target_id: label })
  }

  // Hides links whose target section is not in the DOM, so the nav stays
  // in sync with the actual page content without per-page conditionals.
  pruneOrphanLinks() {
    this.linkTargets.forEach((link) => {
      if (document.querySelector(link.getAttribute("href"))) return

      const item = link.closest(".nav-item") || link
      item.hidden = true
    })
  }

  onScroll() {
    const navHeight = this.element.offsetHeight
    const scrollTop = window.scrollY + 10

    this.updateActiveLinks(scrollTop, navHeight)
    this.updateVisibility(scrollTop, navHeight)
  }

  updateActiveLinks(scrollTop, navHeight) {
    const cursor = scrollTop + navHeight

    // Pick the last linked section whose top has been scrolled past.
    // Keeps the link highlighted across the whole stretch between two
    // anchored sections (even when intermediate content has no nav link).
    let activeLink = null
    let activeTop = -Infinity

    this.linkTargets.forEach((link) => {
      const section = document.querySelector(link.getAttribute("href"))
      if (!section) return

      const sectionTop = section.getBoundingClientRect().top + window.scrollY
      if (sectionTop <= cursor && sectionTop >= activeTop) {
        activeLink = link
        activeTop = sectionTop
      }
    })

    this.linkTargets.forEach((link) => {
      link.classList.toggle("active", link === activeLink)
    })
  }

  updateVisibility(scrollTop, navHeight) {
    if (!this.hasFirstSectionValue) return

    const firstSection = document.querySelector(this.firstSectionValue)
    const pageElement = document.querySelector(this.pageBottomSelectorValue)
    if (!firstSection || !pageElement) return

    const firstSectionTop = firstSection.getBoundingClientRect().top + window.scrollY
    const pageBottom = pageElement.getBoundingClientRect().top + window.scrollY + pageElement.offsetHeight

    const forcedHidden =
      this.hasHideWhenSelectorValue && !!document.querySelector(this.hideWhenSelectorValue)

    const shouldShow =
      !forcedHidden && scrollTop > firstSectionTop - navHeight && scrollTop < pageBottom - navHeight

    this.element.classList.toggle("visible", shouldShow)
    this.element.classList.toggle("invisible", !shouldShow)
  }
}
